'use client';

import { Provider, Region, TrackingCategory, TrackingPreference } from '@coinbase/cookie-manager';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useCompliance } from '../ComplianceProvider/ComplianceProvider';
import { EU_COUNTRIES } from '../ComplianceProvider/constants';

import { cookieManagerConfig } from './config';

// Module-scope constant — inlined by Next.js at build time.
// Each app registers its own consent record with the cookie manager.
const PROJECT_NAME =
  process.env.NEXT_PUBLIC_APP_MODE === 'tokenized-equities'
    ? 'tokenized-equities'
    : 'verified-pools';

type Props = {
  children?: ReactNode;
};
export function CookieManagerProvider({ children }: Props) {
  const [isMounted, setIsMounted] = useState(false);
  const { attestationCountry } = useCompliance();
  const trackingPreference = useRef<TrackingPreference | undefined>();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const region = useMemo(() => {
    if (EU_COUNTRIES.includes(attestationCountry)) {
      return Region.EU;
    }
    return Region.DEFAULT;
  }, [attestationCountry]);

  const setTrackingPreference = useCallback((newPreference: TrackingPreference) => {
    const priorConsent = trackingPreference.current?.consent;
    trackingPreference.current = newPreference;

    if (!priorConsent) {
      // The first time the modal appears, this function is called with nothing present in
      // trackingPreference.current. To avoid an infinite refresh loop, we return early on
      // the first call.
      return;
    }

    const newConsent = newPreference.consent;

    // Best only to reload if the preferences have actually changed.
    const diff = [
      ...priorConsent.filter((elem: TrackingCategory) => !newConsent.includes(elem)),
      ...newConsent.filter((elem: unknown) => !priorConsent.includes(elem)),
    ];

    // reload if the preferences have changed
    if (diff.length > 0) {
      window.location.reload();
    }
  }, []);

  // Provider requires an error handler, but, we don't need it due to RUM collecting errors.
  const handleError = useCallback(() => {}, []);

  if (!isMounted) return null;

  return (
    <Provider
      onError={handleError}
      projectName={PROJECT_NAME}
      locale="en"
      region={region}
      config={cookieManagerConfig}
      log={console.log}
      onPreferenceChange={setTrackingPreference}
    >
      {children}
    </Provider>
  );
}
